import React, { useState } from 'react';
import {
  Container,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SignupAccountStep } from '../../components/Auth/SignupAccountStep';
import { SignupSubscriptionStep } from '../../components/Auth/SignupSubscriptionStep';
import { SignupConfirmationStep } from '../../components/Auth/SignupConfirmationStep';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import { extractErrorMessage, formatErrorForLogging } from '../../utils/errorHandler';
import API_CONFIG from '../../config/apiConfig';

const steps = ['Create Account & Organization', 'Choose Plan', 'Confirmation'];

export interface SignupData {
  // Account
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  
  // Subscription
  subscriptionTier: 'starter' | 'professional' | 'enterprise' | 'industry_specific';
  billingFrequency: 'monthly' | 'annual';
  selectedToolkit?: string; // Toolkit code (e.g., 'MEP_TOOLKIT')
  
  // Organization
  organizationName: string;
  organizationType: 'startup' | 'small_business' | 'enterprise' | 'educational';
  industry: string;
  teamSize: string;
  
  // Terms and Conditions
  acceptTerms: boolean;
  termsVersion?: string;
}

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [signupData, setSignupData] = useState<SignupData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    subscriptionTier: 'starter',
    billingFrequency: 'monthly',
    organizationName: '',
    organizationType: 'startup',
    industry: '',
    teamSize: '1-10',
    acceptTerms: false,
    termsVersion: '1.0.0',
  });

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError(null);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError(null);
  };

  const updateSignupData = (data: Partial<SignupData>) => {
    setSignupData((prev) => ({ ...prev, ...data }));
  };

  const handleComplete = async () => {
    setLoading(true);
    setError(null);

    try {
      // Debug logging
      console.log('Signup data being sent:', {
        email: signupData.email,
        firstName: signupData.firstName,
        lastName: signupData.lastName,
        organizationName: signupData.organizationName,
      });

      // Step 1: Create user account and organization with increased timeout
      const signupResponse = await axios.post(
        `${API_CONFIG.AUTH_SERVICE_URL || API_CONFIG.BASE_URL}/api/v1/auth/signup`,
        {
          email: signupData.email,
          password: signupData.password,
          firstName: signupData.firstName,
          lastName: signupData.lastName,
          organizationName: signupData.organizationName,
          organizationType: signupData.organizationType,
          industry: signupData.industry,
          teamSize: signupData.teamSize,
          acceptTerms: signupData.acceptTerms,
          termsVersion: signupData.termsVersion,
          selectedToolkit: signupData.selectedToolkit || null,
        },
        {
          timeout: 30000, // 30 second timeout
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      const { data: authData } = signupResponse.data;
      const { user } = authData;
      const token = authData.access_token;
      const refreshToken = authData.refresh_token;
      
      // Store auth tokens
      localStorage.setItem('token', token);
      localStorage.setItem('authToken', token);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      
      // Extract organization info from the token payload
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const organizationId = tokenPayload.organizationId;

      // Store organization name for billing setup
      console.log('[SignupPage] Storing billing data in localStorage:', {
        subscriptionTier: signupData.subscriptionTier,
        billingFrequency: signupData.billingFrequency,
        selectedToolkit: signupData.selectedToolkit,
        hasSelectedToolkit: !!signupData.selectedToolkit,
      });
      
      localStorage.setItem('pendingOrganizationName', signupData.organizationName);
      localStorage.setItem('pendingOrganizationId', organizationId || '');
      localStorage.setItem('pendingSubscriptionTier', signupData.subscriptionTier);
      localStorage.setItem('pendingBillingFrequency', signupData.billingFrequency);
      if (signupData.selectedToolkit) {
        console.log('[SignupPage] Storing selectedToolkit:', signupData.selectedToolkit);
        localStorage.setItem('pendingSelectedToolkit', signupData.selectedToolkit);
      } else {
        console.log('[SignupPage] No selectedToolkit to store');
      }

      // Update auth store with complete user data
      const { login } = useAuthStore.getState();
      login({
        id: user.id,
        email: user.email,
        name: user.full_name,
        firstName: user.first_name,
        lastName: user.last_name,
        organizationId: organizationId,
        organizationName: signupData.organizationName,
        organizationRole: tokenPayload.organizationRole || 'owner', // New users are typically owners
        role: user.role || 'user',
      }, token, refreshToken);

      // Navigate to billing setup for new organizations
      navigate('/billing/setup');
    } catch (error: any) {
      console.error('Signup error:', formatErrorForLogging(error));
      
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage);
      
      // If we fail after creating the user, we should clean up
      if (error.response?.status !== 400 && error.response?.status !== 409) {
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <SignupAccountStep
            data={signupData}
            onUpdate={updateSignupData}
            onNext={handleNext}
          />
        );
      case 1:
        return (
          <SignupSubscriptionStep
            data={signupData}
            onUpdate={updateSignupData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <SignupConfirmationStep
            data={signupData}
            onBack={handleBack}
            onComplete={handleComplete}
            loading={loading}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container
      component="main"
      maxWidth="lg"
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 16,
        paddingBottom: 16,
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Typography
          component="h1"
          variant="h4"
          style={{
            marginBottom: 8,
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #325BA8 30%, #4DC4D2 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Welcome to Prismatic Forge
        </Typography>

        <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
          Let's get you started with your productivity transformation
        </Typography>

        <div style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Stepper activeStep={activeStep} style={{ marginBottom: 16 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {error}
            </Alert>
          )}

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {getStepContent(activeStep)}
          </div>
        </div>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Button
              color="primary"
              onClick={() => navigate('/login')}
              style={{ textTransform: 'none', paddingTop: 0, paddingBottom: 0 }}
            >
              Sign in
            </Button>
          </Typography>
        </div>
      </div>
    </Container>
  );
};

export default SignupPage;